import { Button } from "./ui/button";
import { trackEvent } from "@/lib/analytics";
import { saveGameState } from "./game/GamePersistenceV2";
import { useState, useEffect } from "react";
import { getStreakRank } from "@/lib/streakRanks";
import { streakManager } from "./game/managers/StreakManager";
import * as CookieManager from '@/lib/managers/CookieManager';
import { getEffectiveDate, isConsecutiveDay, formatDate } from "@/lib/dateUtils";
import { StreakDisplay } from "./completion/StreakDisplay";
import { isDebugDateMode } from "@/lib/debugUtils";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';

const StreakOverlay = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const { currentStreak: winStreak, maxStreak } = streakManager.getStreakState();
  
  useEffect(() => {
    // Get dates for comparison
    // Use mode-aware storage for streak data
    const lastWinDate = modeAwareStorage.getCookie('last_win_date');
    const lastPlayedDate = modeAwareStorage.getCookie('last_played');
    const effectiveDate = getEffectiveDate();
    const debugMode = isDebugDateMode();
    
    console.log('Streak overlay checking dates:', {
      lastWinDate,
      lastPlayedDate,
      effectiveDate: effectiveDate.toISOString(),
      debugMode,
      currentStreak: winStreak,
      timestamp: new Date().toISOString()
    });
    
    if (lastWinDate) {
      const lastWin = new Date(lastWinDate);
      
      // Show animation if:
      // 1. We have a consecutive day streak OR same day streak
      // 2. AND we have a positive streak value
      const isValidStreak = isConsecutiveDay(lastWin, effectiveDate) || 
                           formatDate(lastWin) === formatDate(effectiveDate);
      
      // Additional check for debug mode - show streak if we have one
      const shouldShowInDebug = debugMode && winStreak > 0;
      
      setShowAnimation((isValidStreak || shouldShowInDebug) && winStreak > 0);
      
      console.log('Streak animation decision:', {
        isValidStreak,
        shouldShowInDebug,
        showAnimation: (isValidStreak || shouldShowInDebug) && winStreak > 0,
        timestamp: new Date().toISOString()
      });
    }
  }, [winStreak]);

  const handleStartPlaying = () => {
    saveGameState(
      [], // guessHistory
      [], // levelAttempts
      "playing", // gameStatus
      "", // currentWord
      1, // starting level
      [], // guesses
      0, // currentGuessRow
      {}, // keyboardStatus
      null, // previousWord
      false // isTransitioning
    );
    
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      <div 
        className="overlay-container fixed left-1/2 top-1/2 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 space-y-6 rounded-xl bg-gray-900 p-6 shadow-xl" 
        style={{ 
          border: "5px solid #454546", 
          borderWidth: "5px", 
        }}
      >
        <div className="flex justify-center mb-4">
          <img 
            src="/lovable-uploads/f8f44bc9-ce01-42f1-98ff-b6aad7a934e9.png"
            alt="Babblegram Logo" 
            className="h-16 animate-fade-in"
          />
        </div>
        
        <h2 className="text-2xl font-bold text-center text-[#de0046]">Welcome Back!</h2>
        
        <div className="space-y-8">
          {(winStreak > 0 || maxStreak > 0) && showAnimation && (
            <StreakDisplay 
              currentStreak={winStreak} 
              previousStreak={0} 
              savedGameStatus="won"
            />
          )}

          <div className="text-right text-gray-300">
            {winStreak > 0 ? (
              <>
                <p className="text-lg mb-2">Keep playing to rank up!</p>
                {maxStreak > 0 && (
                  <p className="text-sm text-gray-400">
                    Your best streak is {maxStreak} {maxStreak === winStreak && "(Current)"}
                  </p>
                )}
              </>
            ) : maxStreak > 0 ? (
              <p className="text-lg">Start your journey back to the top!</p>
            ) : (
              <p className="text-lg">Start your journey to become a Babblegram Grandmaster!</p>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={() => { trackEvent('start_playing', { source: 'streak_overlay' }); handleStartPlaying(); }} className="bg-[#de0046] hover:bg-[#de0046]/90">
            Start Playing
          </Button>
        </div>

        <div className="text-gray-500 text-xs text-center w-full">
          © {new Date().getFullYear()} BabbleGram. Created by Dara Walsh
        </div>
      </div>
    </div>
  );
};

export default StreakOverlay;
